import { useState, useEffect } from "react";
import { getAuthToken } from "../../../utils/auth";
import { toast } from "react-toastify";
import "../../../style/HubSchedule.css";
import "../../../style/designer-milling-responsive.css";
import { apiAxios } from "../../../utils/apiAxios";

interface Time {
  hour: string;
  minute: string;
  period: "AM" | "PM";
}

interface DaySchedule {
  day: string;
  startTime: Time;
  endTime: Time;
  offDay: boolean;
}

const createTime = (): Time => ({
  hour: "9",
  minute: "00",
  period: "AM"
});

const initialSchedule: DaySchedule[] = [
  { day: "Monday", startTime: createTime(), endTime: { hour: "5", minute: "00", period: "PM" }, offDay: true },
  { day: "Tuesday", startTime: createTime(), endTime: { hour: "5", minute: "00", period: "PM" }, offDay: true },
  { day: "Wednesday", startTime: createTime(), endTime: createTime(), offDay: true },
  { day: "Thursday", startTime: { hour: "10", minute: "00", period: "AM" }, endTime: { hour: "6", minute: "00", period: "PM" }, offDay: true },
  { day: "Friday", startTime: { hour: "10", minute: "00", period: "AM" }, endTime: { hour: "6", minute: "00", period: "PM" }, offDay: true },
  { day: "Saturday", startTime: createTime(), endTime: { hour: "2", minute: "00", period: "PM" }, offDay: true },
  { day: "Sunday", startTime: createTime(), endTime: createTime(), offDay: true }
];

const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
const minutes = ["00", "15", "30", "45"];

const convertTo12Hour = (time: string | null): Time => {
  if (!time) return { hour: "9", minute: "00", period: "AM" };
  let [hour, minute] = time.split(":");
  let h = parseInt(hour);
  let period: "AM" | "PM" = "AM";
  if (h >= 12) {
    period = "PM";
    if (h > 12) h -= 12;
  }
  if (h === 0) h = 12;
  return { hour: h.toString(), minute: minute, period };
};

const convertTo24 = (time: any) => {
  let hour = parseInt(time.hour);
  if (time.period === "PM" && hour !== 12) hour += 12;
  if (time.period === "AM" && hour === 12) hour = 0;
  return `${hour}:${time.minute}:00`;
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DesignerSchedule = () => {
  const [schedule, setSchedule] = useState(initialSchedule);

  const updateTime = (index: number, type: "startTime" | "endTime", field: keyof Time, value: string) => {
    const updated = [...schedule];
    (updated[index][type] as any)[field] = value;
    setSchedule(updated);
  };

  const toggleOffDay = (index: number) => {
    const updated = [...schedule];
    updated[index].offDay = !updated[index].offDay;
    setSchedule(updated);
  };

  const loadSchedule = async () => {
    const token = getAuthToken();
    const response = await apiAxios.get(`${API_BASE_URL}/Desginer/GetDesginerTiming`, {
      headers: { AuthorizationData: token }
    });
    const data = response.data;

    const updatedSchedule = initialSchedule.map((dayItem) => {
      const apiDay = data.find((x: any) => x.dayOfWeek === dayItem.day);
      if (!apiDay) return dayItem;
      return {
        day: dayItem.day,
        offDay: apiDay.isOffDay,
        startTime: convertTo12Hour(apiDay.startTime),
        endTime: convertTo12Hour(apiDay.endTime)
      };
    });
    setSchedule(updatedSchedule);
  };

  useEffect(() => {
    loadSchedule();
  }, []);

  const handleSave = async () => {
    try {
      const payload = schedule.map((d) => ({
        hubId: 10,
        dayOfWeek: d.day,
        startTime: d.offDay ? null : convertTo24(d.startTime),
        endTime: d.offDay ? null : convertTo24(d.endTime),
        isOffDay: d.offDay
      }));

      await apiAxios.post(
        `${API_BASE_URL}/Desginer/SaveDesginerTiming`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            AuthorizationData: getAuthToken()
          }
        }
      );

      toast.success("Timing saved successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to save timing");
    }
  };

  return (
    <div className="container-fluid mt-4">
      <h4 className="mb-4">Schedule</h4>
      <div className="premium-card">



        <div className="table-responsive mt-3">
          <table className="schedule-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Off Day</th>
              </tr>
            </thead>

            <tbody>
              {schedule.map((item, index) => (
                <tr key={item.day} className={item.offDay ? "off-day-row" : ""}>
                  <td className="day-name">{item.day}</td>

                  <td>
                    <div className="time-select">
                      <select disabled={item.offDay} value={item.startTime.hour} onChange={(e) => updateTime(index, "startTime", "hour", e.target.value)}>
                        {hours.map(h => <option key={h}>{h}</option>)}
                      </select>

                      <select disabled={item.offDay} value={item.startTime.minute} onChange={(e) => updateTime(index, "startTime", "minute", e.target.value)}>
                        {minutes.map(m => <option key={m}>{m}</option>)}
                      </select>

                      <select disabled={item.offDay} value={item.startTime.period} onChange={(e) => updateTime(index, "startTime", "period", e.target.value)}>
                        <option>AM</option>
                        <option>PM</option>
                      </select>
                    </div>
                  </td>

                  <td>
                    <div className="time-select">
                      <select disabled={item.offDay} value={item.endTime.hour} onChange={(e) => updateTime(index, "endTime", "hour", e.target.value)}>
                        {hours.map(h => <option key={h}>{h}</option>)}
                      </select>

                      <select disabled={item.offDay} value={item.endTime.minute} onChange={(e) => updateTime(index, "endTime", "minute", e.target.value)}>
                        {minutes.map(m => <option key={m}>{m}</option>)}
                      </select>

                      <select disabled={item.offDay} value={item.endTime.period} onChange={(e) => updateTime(index, "endTime", "period", e.target.value)}>
                        <option>AM</option>
                        <option>PM</option>
                      </select>
                    </div>
                  </td>

                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={item.offDay}
                      onChange={() => toggleOffDay(index)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-start mt-3">
          <button className="btn btn-gradient" onClick={handleSave}>
            Save Schedule
          </button>
        </div>

      </div>

    </div>
  );
};

export default DesignerSchedule;